import React from 'react';
import { useTranslations } from 'next-intl';
import { ArrowTopRight2 } from 'assets/icons/ArrowTopRight2';
import {
  DirectionsLink,
  PhoneLink,
} from 'components/performance-lab/performanceLabContactLinks';
import {
  PERFORMANCE_LAB_MAPS_EMBED_URL,
  PERFORMANCE_LAB_MAPS_URL,
} from 'components/performance-lab/performanceLabMaps';
import {
  PlContentShell,
  PlPillarBody,
  PlPillarTitle,
  PlSectionRoot,
  SectionContent,
} from './sectionLayout';
import styles from './GetDirectionsSection.module.css';

type GetDirectionsSectionProps = {
  variant?: 'default' | 'contact';
  embedded?: boolean;
  weekdayHours?: string;
  sundayHours?: string;
};

export const GetDirectionsSection: React.FC<GetDirectionsSectionProps> = ({
  variant = 'default',
  embedded = false,
  weekdayHours,
  sundayHours,
}) => {
  const t = useTranslations('performance-lab');
  const isContact = variant === 'contact';

  const findUsBlock = (
    <div
      className={styles.infoBlock}
      data-contact={isContact ? 'true' : undefined}
    >
      <PlPillarTitle>{t('unifiedExperience.findUs')}</PlPillarTitle>
      <PlPillarBody className={styles.addressText}>
        {t('unifiedExperience.venueName')}
        <br />
        {t('unifiedExperience.addressLine1')}
        <br />
        {t('unifiedExperience.addressLine2')}
      </PlPillarBody>
      <PhoneLink
        href="tel:+918047282422"
        aria-label={t('getDirections.phoneAria')}
        data-buttontype="performance_lab_call"
      >
        {t('getDirections.phoneText')}
      </PhoneLink>
    </div>
  );

  const workingHoursBlock = (
    <div
      className={styles.infoBlock}
      data-contact={isContact ? 'true' : undefined}
    >
      <PlPillarTitle>
        {t('unifiedExperience.workingHours')}
      </PlPillarTitle>
      <div className={styles.hoursGroup}>
        {/* ponytail: GLP passes its own single set of hours, so it keeps the
            plain Mon-Sat / Sunday shape instead of the diagnostics/recovery split */}
        {weekdayHours || sundayHours ? (
          <>
            <PlPillarBody className={styles.addressText}>
              {t('unifiedExperience.hoursLine1')}
              <br />
              {weekdayHours ?? t('unifiedExperience.hoursLine2')}
            </PlPillarBody>
            <PlPillarBody className={styles.addressText}>
              {t('unifiedExperience.hoursLine3')}
              <br />
              {sundayHours ?? t('unifiedExperience.hoursLine4')}
            </PlPillarBody>
          </>
        ) : (
          <>
            <div className={styles.hoursGroupBlock}>
              <PlPillarBody className={styles.hoursLabel}>
                {t('unifiedExperience.diagnosticsLabel')}
              </PlPillarBody>
              <div className={styles.hoursSlotGroup}>
                <div className={styles.hoursDayTime}>
                  <PlPillarBody className={styles.addressText}>
                    {t('unifiedExperience.diagnosticsDays')}
                  </PlPillarBody>
                  <PlPillarBody className={styles.addressText}>
                    {t('unifiedExperience.diagnosticsHours')}
                  </PlPillarBody>
                </div>
                <PlPillarBody className={styles.hoursMuted}>
                  {t('unifiedExperience.diagnosticsSunday')}
                </PlPillarBody>
              </div>
            </div>
            <div className={styles.hoursGroupBlock}>
              <PlPillarBody className={styles.hoursLabel}>
                {t('unifiedExperience.recoveryLabel')}
              </PlPillarBody>
              <div className={styles.hoursSlotStack}>
                <div className={styles.hoursSlotGroup}>
                  <div className={styles.hoursDayTime}>
                    <PlPillarBody className={styles.addressText}>
                      {t('unifiedExperience.recoveryDaysWeekday')}
                    </PlPillarBody>
                    <PlPillarBody className={styles.addressText}>
                      {t('unifiedExperience.recoveryHoursWeekday')}
                    </PlPillarBody>
                  </div>
                  <PlPillarBody className={styles.hoursMuted}>
                    {t('unifiedExperience.recoverySlotWeekday')}
                  </PlPillarBody>
                </div>
                <div className={styles.hoursSlotGroup}>
                  <div className={styles.hoursDayTime}>
                    <PlPillarBody className={styles.addressText}>
                      {t('unifiedExperience.recoveryDaysSunday')}
                    </PlPillarBody>
                    <PlPillarBody className={styles.addressText}>
                      {t('unifiedExperience.recoveryHoursSunday')}
                    </PlPillarBody>
                  </div>
                  <PlPillarBody className={styles.hoursMuted}>
                    {t('unifiedExperience.recoverySlotSunday')}
                  </PlPillarBody>
                </div>
              </div>
            </div>
            <PlPillarBody
              className={`${styles.hoursMuted} ${styles.hoursNote}`}
            >
              {t('unifiedExperience.hoursNote')}
            </PlPillarBody>
          </>
        )}
      </div>
    </div>
  );

  const mapBlock = (
    <div
      className={styles.mapColumn}
      data-contact={isContact ? 'true' : undefined}
    >
      <div
        className={styles.mapCard}
        data-contact={isContact ? 'true' : undefined}
      >
        <iframe
          className={styles.mapIframe}
          title={t('getDirections.mapTitle')}
          src={PERFORMANCE_LAB_MAPS_EMBED_URL}
          loading="eager"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </div>
      <DirectionsLink
        href={PERFORMANCE_LAB_MAPS_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('getDirections.linkAria')}
        data-buttontype="performance_lab_get_directions"
      >
        {t('getDirections.linkText')}
        <ArrowTopRight2 fill="#000" aria-hidden />
      </DirectionsLink>
    </div>
  );

  const layout = isContact ? (
    <PlContentShell className={styles.layoutShell} data-contact="true">
      {findUsBlock}
      {workingHoursBlock}
      {mapBlock}
    </PlContentShell>
  ) : (
    <PlContentShell className={styles.layoutShell}>
      <div className={styles.infoColumn}>
        {findUsBlock}
        {workingHoursBlock}
      </div>
      {mapBlock}
    </PlContentShell>
  );

  const content = isContact ? (
    <SectionContent className={styles.contactSectionContent}>
      {layout}
    </SectionContent>
  ) : (
    <SectionContent>{layout}</SectionContent>
  );

  if (embedded) {
    return (
      <div className={styles.embeddedRoot} aria-label={t('getDirections.aria')}>
        {content}
      </div>
    );
  }

  return (
    <PlSectionRoot aria-label={t('getDirections.aria')}>
      {content}
    </PlSectionRoot>
  );
};
